import React, { useEffect, useMemo, useRef, useState } from "react";
import { Laptop2, Plus, X, Loader2, Power, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KINDS = [
    { v: "desktop_app", label: "MoovBytes Desktop" },
    { v: "pc", label: "PC" },
    { v: "server", label: "Server" },
    { v: "nas", label: "NAS" },
    { v: "other", label: "Other" },
];

const ROLE_RANK = { viewer: 1, auditor: 2, engineer: 3, admin: 4, superadmin: 5 };

export default function PortalDevices() {
    const { user } = useAuth();
    const canManage = (ROLE_RANK[user?.role] || 0) >= ROLE_RANK.engineer;

    const [devices, setDevices] = useState([]);
    const [status, setStatus] = useState("active");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ device_name: "", kind: "desktop_app" });
    const [submitting, setSubmitting] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef(null);

    const refresh = async (s = status) => {
        setLoading(true);
        try {
            const { data } = await http.get(`/licensing/devices?status=${s}`);
            setDevices(data.devices || []);
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { refresh(status); }, [status]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return devices;
        return devices.filter((d) => (d.device_name || "").toLowerCase().includes(q));
    }, [devices, search]);

    const activate = async (e) => {
        e.preventDefault();
        if (!form.device_name.trim()) return;
        setSubmitting(true);
        try {
            const fingerprint = `manual-${crypto.randomUUID()}`;
            await http.post("/licensing/activate", {
                device_name: form.device_name.trim(),
                device_fingerprint: fingerprint,
                kind: form.kind,
            });
            toast.success("Device activated.");
            setForm({ device_name: "", kind: "desktop_app" });
            setShowForm(false);
            refresh(status);
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setSubmitting(false);
        }
    };

    const deactivate = async (id) => {
        if (!window.confirm("Deactivate this device? It will free up a license seat.")) return;
        setBusyId(id);
        try {
            await http.post(`/licensing/devices/${id}/deactivate`);
            toast.success("Device deactivated.");
            refresh(status);
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setBusyId(null);
        }
    };

    const reactivate = async (d) => {
        setBusyId(d.id);
        try {
            await http.post("/licensing/activate", {
                device_name: d.device_name,
                device_fingerprint: d.device_fingerprint,
                kind: d.kind,
            });
            toast.success("Device reactivated.");
            refresh(status);
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setBusyId(null);
        }
    };

    // §26 offline activation: the request file (device_name/fingerprint/kind,
    // unsigned -- Desktop has no private key) came from a machine with no
    // internet access. Activating it here goes through the exact same
    // POST /licensing/activate as the live one-click path; the only
    // difference is the result gets downloaded as a file to carry back
    // and import into that offline Desktop, instead of being consumed
    // in-process by a live IPC round trip.
    const importOfflineRequest = async (file) => {
        setImporting(true);
        try {
            const text = await file.text();
            let req;
            try {
                req = JSON.parse(text);
            } catch {
                throw new Error("That file isn't valid JSON.");
            }
            if (!req.device_fingerprint || !req.device_name) {
                throw new Error("That file doesn't look like a MoovBytes activation request.");
            }
            const { data } = await http.post("/licensing/activate", {
                device_name: req.device_name,
                device_fingerprint: req.device_fingerprint,
                kind: req.kind || "desktop_app",
            });
            if (!data.entitlement) throw new Error("Activation succeeded but no entitlement was issued.");

            const blob = new Blob(
                [JSON.stringify({ entitlement: data.entitlement, activation_id: data.id, device_name: data.device_name }, null, 2)],
                { type: "application/json" },
            );
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "moovbytes-activation.json";
            a.click();
            URL.revokeObjectURL(url);

            toast.success("Activation file downloaded. Import it on the offline device.");
            refresh(status);
        } catch (e) {
            toast.error(e?.response?.data?.detail?.error === "plan_limit_exceeded"
                ? "Activation limit reached. Deactivate a device or upgrade your plan."
                : e?.response?.data?.detail || e.message || "Import failed.");
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    return (
        <div className="p-6 sm:p-9" data-testid="portal-devices-page">
            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                        Licensed devices
                    </div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Devices</h1>
                </div>
                {canManage && (
                    <div className="flex items-center gap-2">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/json"
                            className="hidden"
                            data-testid="portal-devices-import-file-input"
                            onChange={(e) => e.target.files?.[0] && importOfflineRequest(e.target.files[0])}
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={importing}
                            data-testid="portal-devices-import-btn"
                            title="Activate a device that has no internet access, from its exported activation request"
                            className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground disabled:opacity-50"
                        >
                            {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Import offline activation
                        </button>
                        <button
                            onClick={() => setShowForm((v) => !v)}
                            data-testid="portal-devices-add-btn"
                            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white hover:opacity-90"
                        >
                            {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Cancel" : "Add device"}
                        </button>
                    </div>
                )}
            </div>

            {showForm && (
                <form
                    onSubmit={activate}
                    data-testid="portal-devices-add-form"
                    className="mb-5 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-surface p-4"
                >
                    <div className="flex-1 min-w-[220px]">
                        <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            Device name
                        </label>
                        <input
                            required
                            value={form.device_name}
                            onChange={(e) => setForm({ ...form, device_name: e.target.value })}
                            placeholder="e.g. Ops Laptop — Jordan"
                            data-testid="portal-devices-name-input"
                            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                    </div>
                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            Type
                        </label>
                        <select
                            value={form.kind}
                            onChange={(e) => setForm({ ...form, kind: e.target.value })}
                            data-testid="portal-devices-kind-select"
                            className="mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                        >
                            {KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
                        </select>
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="portal-devices-submit-btn"
                        className="rounded-md bg-brand px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white hover:opacity-90 disabled:opacity-50"
                    >
                        {submitting ? <Loader2 size={14} className="mr-1.5 inline animate-spin" /> : null}
                        Activate
                    </button>
                </form>
            )}

            <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="flex gap-1 rounded-md border border-border bg-surface p-1" data-testid="portal-devices-status-tabs">
                    {["active", "deactivated", "all"].map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatus(s)}
                            data-testid={`portal-devices-tab-${s}`}
                            className={[
                                "rounded px-3 py-1.5 text-xs font-medium capitalize",
                                status === s ? "bg-brand text-white" : "text-muted-foreground hover:text-foreground",
                            ].join(" ")}
                        >
                            {s}
                        </button>
                    ))}
                </div>
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search devices…"
                    data-testid="portal-devices-search"
                    className="ml-auto w-64 rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
            </div>

            <div className="rounded-lg border border-border bg-surface">
                <table className="w-full text-sm" data-testid="portal-devices-table">
                    <thead className="border-b border-border">
                        <tr className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            <th className="px-4 py-2.5 text-left">Device</th>
                            <th className="px-4 py-2.5 text-left">Type</th>
                            <th className="px-4 py-2.5 text-left">Status</th>
                            <th className="px-4 py-2.5 text-left">Activated</th>
                            <th className="px-4 py-2.5 text-left">Last seen</th>
                            <th className="px-4 py-2.5 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                                <Loader2 size={14} className="mr-2 inline animate-spin" /> Loading devices…
                            </td></tr>
                        )}
                        {!loading && filtered.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                                <Laptop2 size={22} className="mx-auto mb-2 opacity-40" />
                                No devices yet.
                            </td></tr>
                        )}
                        {filtered.map((d) => (
                            <tr key={d.id} className="border-b border-border/60 last:border-0 hover:bg-muted/40" data-testid={`portal-device-row-${d.id}`}>
                                <td className="px-4 py-2.5 font-medium">{d.device_name}</td>
                                <td className="px-4 py-2.5 text-muted-foreground">
                                    {(KINDS.find((k) => k.v === d.kind) || {}).label || d.kind}
                                </td>
                                <td className="px-4 py-2.5">
                                    <span className={`mb-pill ${d.status === "active" ? "border-emerald-500/40 text-emerald-500" : "border-border text-muted-foreground"}`}>
                                        {d.status}
                                    </span>
                                </td>
                                <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">
                                    {(d.activated_at || "").replace("T", " ").slice(0, 19)}
                                </td>
                                <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">
                                    {(d.last_seen_at || "").replace("T", " ").slice(0, 19)}
                                </td>
                                <td className="px-4 py-2.5 text-right">
                                    {canManage && d.status === "active" && (
                                        <button
                                            onClick={() => deactivate(d.id)}
                                            disabled={busyId === d.id}
                                            data-testid={`portal-device-deactivate-${d.id}`}
                                            className="inline-flex items-center gap-1 rounded-sm border border-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest hover:border-rose-400 hover:text-rose-400 disabled:opacity-50"
                                        >
                                            <Power size={10} /> Deactivate
                                        </button>
                                    )}
                                    {canManage && d.status !== "active" && (
                                        <button
                                            onClick={() => reactivate(d)}
                                            disabled={busyId === d.id}
                                            data-testid={`portal-device-reactivate-${d.id}`}
                                            className="inline-flex items-center gap-1 rounded-sm border border-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest hover:border-emerald-400 hover:text-emerald-500 disabled:opacity-50"
                                        >
                                            <RefreshCw size={10} /> Reactivate
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
